/**
 * Passcode Setup Page
 * Allows user to create a new PIN (4-8 digits) or Password (6+ characters)
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePin } from '../context/PinContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Lock, Eye, EyeOff, AlertTriangle, Hash, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function PinSetupPage() {
  const navigate = useNavigate();
  const { setupPasscode, isPinSet } = usePin();
  const [passcodeType, setPasscodeType] = useState('pin'); // 'pin' or 'password'
  const [passcode, setPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [showConfirmPasscode, setShowConfirmPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Handle redirect in useEffect to avoid React navigation errors
  useEffect(() => {
    if (isPinSet) {
      navigate('/unlock', { replace: true });
    }
  }, [isPinSet, navigate]);

  // If passcode already set, show nothing while redirecting
  if (isPinSet) {
    return null;
  }

  const minLength = passcodeType === 'password' ? 6 : 4;
  const maxLength = passcodeType === 'password' ? 64 : 8;

  const validatePasscode = (value) => {
    if (passcodeType === 'pin') {
      if (!/^\d*$/.test(value)) {
        return 'PIN must contain only digits';
      }
      if (value.length > 0 && value.length < 4) {
        return 'PIN must be at least 4 digits';
      }
      if (value.length > 8) {
        return 'PIN must be at most 8 digits';
      }
    } else {
      if (value.length > 0 && value.length < 6) {
        return 'Password must be at least 6 characters';
      }
    }
    return '';
  };

  const handlePasscodeChange = (e) => {
    let value = e.target.value;
    if (passcodeType === 'pin') {
      value = value.replace(/\D/g, '').slice(0, 8);
    } else {
      value = value.slice(0, 64);
    }
    setPasscode(value);
    setError('');
  };

  const handleConfirmPasscodeChange = (e) => {
    let value = e.target.value;
    if (passcodeType === 'pin') {
      value = value.replace(/\D/g, '').slice(0, 8);
    } else {
      value = value.slice(0, 64);
    }
    setConfirmPasscode(value);
    setError('');
  };

  const handleTypeChange = (type) => {
    setPasscodeType(type);
    setPasscode('');
    setConfirmPasscode('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passcode
    const passcodeError = validatePasscode(passcode);
    if (passcodeError) {
      setError(passcodeError);
      return;
    }

    if (passcode.length < minLength) {
      setError(`${passcodeType === 'pin' ? 'PIN' : 'Password'} must be at least ${minLength} ${passcodeType === 'pin' ? 'digits' : 'characters'}`);
      return;
    }

    if (passcode !== confirmPasscode) {
      setError(`${passcodeType === 'pin' ? 'PINs' : 'Passwords'} do not match`);
      return;
    }

    setLoading(true);
    try {
      const result = await setupPasscode(passcode, passcodeType);
      if (result.success) {
        toast.success(`${passcodeType === 'pin' ? 'PIN' : 'Password'} created successfully!`);
        navigate('/dashboard', { replace: true });
      } else {
        setError(result.error || 'Failed to set passcode. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStrengthInfo = () => {
    if (passcodeType === 'pin') {
      if (passcode.length === 0) return { text: 'Enter 4-8 digits', color: '' };
      if (passcode.length < 4) return { text: `${4 - passcode.length} more digits needed`, color: 'text-red-500' };
      if (passcode.length < 6) return { text: 'Good - add more for stronger security', color: 'text-yellow-500' };
      return { text: 'Strong PIN', color: 'text-green-500' };
    } else {
      if (passcode.length === 0) return { text: 'Enter at least 6 characters', color: '' };
      if (passcode.length < 6) return { text: `${6 - passcode.length} more characters needed`, color: 'text-red-500' };
      if (passcode.length < 10) return { text: 'Good - add more for stronger security', color: 'text-yellow-500' };
      return { text: 'Strong password', color: 'text-green-500' };
    }
  };

  const strengthInfo = getStrengthInfo();

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md flex-1 flex flex-col justify-center">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/20 rounded-sm mb-4">
            <Shield className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
            ARMS ROOM
          </h1>
          <p className="text-lg font-semibold text-muted-foreground mt-2">
            Secure Firearm Inventory Management
          </p>
          {/* App Description */}
          <p className="mt-4 text-sm text-muted-foreground/80 leading-relaxed max-w-sm mx-auto">
            A private firearm inventory that stays on your device. Log firearms, ammo counts, accessories, and notes. Attach photos and documents for easy reference.
          </p>
          <p className="mt-2 text-sm font-medium text-muted-foreground/90">
            No accounts. No cloud. Just your inventory.
          </p>
        </div>

        {/* Passcode Setup Card */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl flex items-center justify-center gap-2">
              <Lock className="w-5 h-5 text-secondary" />
              Create Your Passcode
            </CardTitle>
            <CardDescription>
              Choose a PIN or password to protect your inventory
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Passcode Type Toggle */}
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                variant={passcodeType === 'pin' ? 'default' : 'outline'}
                className={`flex-1 ${passcodeType === 'pin' ? 'bg-secondary text-secondary-foreground' : ''}`}
                onClick={() => handleTypeChange('pin')}
              >
                <Hash className="w-4 h-4 mr-2" />
                Use PIN
              </Button>
              <Button
                type="button"
                variant={passcodeType === 'password' ? 'default' : 'outline'}
                className={`flex-1 ${passcodeType === 'password' ? 'bg-secondary text-secondary-foreground' : ''}`}
                onClick={() => handleTypeChange('password')}
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Use Password
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Passcode Input */}
              <div className="space-y-2">
                <Label htmlFor="passcode">
                  {passcodeType === 'pin' ? 'PIN (4-8 digits)' : 'Password (6+ characters)'}
                </Label>
                <div className="relative">
                  <Input
                    id="passcode"
                    type={showPasscode ? 'text' : 'password'}
                    value={passcode}
                    onChange={handlePasscodeChange}
                    placeholder={passcodeType === 'pin' ? 'Enter PIN' : 'Enter password'}
                    className="pr-10 text-center text-2xl tracking-widest font-mono"
                    inputMode={passcodeType === 'pin' ? 'numeric' : 'text'}
                    autoComplete="off"
                    data-testid="pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasscode(!showPasscode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Confirm Passcode Input */}
              <div className="space-y-2">
                <Label htmlFor="confirmPasscode">
                  Confirm {passcodeType === 'pin' ? 'PIN' : 'Password'}
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPasscode"
                    type={showConfirmPasscode ? 'text' : 'password'}
                    value={confirmPasscode}
                    onChange={handleConfirmPasscodeChange}
                    placeholder={passcodeType === 'pin' ? 'Confirm PIN' : 'Confirm password'}
                    className="pr-10 text-center text-2xl tracking-widest font-mono"
                    inputMode={passcodeType === 'pin' ? 'numeric' : 'text'}
                    autoComplete="off"
                    data-testid="confirm-pin-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPasscode(!showConfirmPasscode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Strength indicator */}
              <div className="flex gap-1">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      i < passcode.length
                        ? passcode.length >= (passcodeType === 'pin' ? 6 : 10)
                          ? 'bg-green-500'
                          : passcode.length >= minLength
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                        : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <p className={`text-xs text-center ${strengthInfo.color || 'text-muted-foreground'}`}>
                {strengthInfo.text}
              </p>

              <Button
                type="submit"
                disabled={loading || passcode.length < minLength || passcode !== confirmPasscode}
                className="w-full h-12 rounded-sm"
                data-testid="setup-pin-btn"
              >
                {loading ? 'Setting up...' : `Set ${passcodeType === 'pin' ? 'PIN' : 'Password'} & Continue`}
              </Button>
            </form>

            {/* Security notice */}
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-sm mt-6">
              <Shield className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Local-Only Security</p>
                <p>
                  Your passcode is stored securely on this device only. 
                  There is no account recovery - if you forget your passcode, 
                  you must reset the app which erases all data.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm font-bold text-muted-foreground mt-6">
          YOUR DATA IS ONLY STORED ON THIS DEVICE
        </p>
      </div>

      {/* Version */}
      <footer className="py-4 text-center">
        <span className="text-xs text-muted-foreground/60">Version 1.1</span>
      </footer>
    </div>
  );
}
